import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { spaTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { parseSpaBooleanField } from "../../../constants/SpaBooleanFields";

const SpaCancelReasons = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [cancelReasonDescription, setCancelReasonDescription] = useState<
    string | null
  >(null);
  const [active, setActive] = useState<boolean>(true);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [cancelReasonError, setCancelReasonError] = useState<boolean>(false);
  const [cancelReasonErrorMessage, setCancelReasonErrorMessage] =
    useState<string>("");

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);
  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateCancelReasonField = (): boolean => {
    if (!cancelReasonDescription || cancelReasonDescription.length === 0) {
      setAlertMessage("Cancel reasons are mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setCancelReasonError(false);
    setCancelReasonErrorMessage("");
    if (
      cancelReasonDescription &&
      !validateString(
        cancelReasonDescription,
        validationLength.cancelReason,
        validationType.SPACE,
      )
    ) {
      setCancelReasonError(true);
      setCancelReasonErrorMessage(validationMessages.cancelReason);
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(
        listForDuplicate,
        DNDFieldsEnum.CANCELREASONDESCRIPTION,
        cancelReasonDescription,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  const updateItem = () => {
    if (!validateCancelReasonField()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.cancelReasons} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateCancelReasonField()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setCancelReasonDescription(list["Cancel Reason Description"] ?? null);
      setActive(parseSpaBooleanField(list?.Active));
    } else {
      setCancelReasonDescription(null);
      setActive(true);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Cancel Reason Description": cancelReasonDescription,
    Active: active,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Cancel Reasons
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.cancelReasonHeader ?? "Cancel Reasons",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Cancel Reason Description</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.cancelReasonCancelReason,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Cancel Reason Description"
              value={cancelReasonDescription ?? ""}
              onChange={(e) =>
                setCancelReasonDescription(e.target.value || null)
              }
              helperText={cancelReasonError ? cancelReasonErrorMessage : ""}
              error={cancelReasonError}
              fullWidth
            />
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active"
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Cancel Reasons
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Cancel Reasons Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.CANCELREASONDESCRIPTION}
              name={DNDFieldsEnum.ACTIVE}
            />
          ) : (
            <Typography variant="body2">No Cancel Reasons Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaCancelReasons;
